import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, AlertTriangle, AlertCircle, ExternalLink, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const changeLog = [
  { id: 'CHG-2026-0088', time: 'Apr 15, 14:28', type: 'Prompt Policy', target: 'Legal Drafting · Claude Sonnet', changedBy: 'legal-team@meridian.global', status: 'Pending Approval', enforced: 'Pending' },
  { id: 'CHG-2026-0087', time: 'Apr 15, 12:00', type: 'Data Source', target: 'Training Corpus · GPT-4o', changedBy: 'data-team@meridian.global', status: 'Approved', enforced: 'Approved' },
  { id: 'CHG-2026-0086', time: 'Apr 14, 16:33', type: 'Prompt Policy', target: 'Legal Drafting · Claude Sonnet', changedBy: 'ldrs-dev@meridian.global', status: 'Unauthorized', enforced: 'Triggered Incident' },
  { id: 'CHG-2026-0085', time: 'Apr 14, 09:00', type: 'Model Version', target: 'GPT-4o · 2025-10 → 2025-11', changedBy: 'infra-team@meridian.global', status: 'Approved', enforced: 'Passed' },
  { id: 'CHG-2026-0084', time: 'Apr 13, 15:30', type: 'Agent Permission', target: 'audit-assistant-01 · Read access', changedBy: 'compliance-team@meridian.global', status: 'Approved', enforced: 'Passed' },
];

const infraChanges = [
  { id: 'INF-001', service: 'S3', desc: 'S3 bucket public access enabled on mglobal-prod-ai-training-data', account: 'mglobal-prod-ai', severity: 'CRITICAL', time: '12 min ago' },
  { id: 'INF-002', service: 'EC2', desc: 'Security group 0.0.0.0/0 ingress on port 22 added to sg-ai-inference', account: 'mglobal-prod-ai', severity: 'CRITICAL', time: '28 min ago' },
  { id: 'INF-003', service: 'IAM', desc: 'IAM policy with wildcard permissions attached to role ai-pipeline-executor', account: 'mglobal-staging', severity: 'HIGH', time: '1 hour ago' },
];

const rules = [
  { title: 'Block production promotion if evidence coverage below 80%', status: 'Active', blocking: '0 models' },
  { title: 'Require named approval for model, prompt, or data-source changes in high-risk workflows', status: 'Active', blocking: '1 pending (CHG-2026-0088)' },
  { title: 'Open incident automatically for unauthorized production model drift', status: 'Active', blocking: '1 triggered today (CHG-2026-0086)' },
];

const enforcedColors = {
  'Triggered Incident': { color: '#EF4444', bg: 'bg-[#EF4444]/10' },
  'Pending': { color: '#F59E0B', bg: 'bg-[#F59E0B]/10' },
  'Approved': { color: '#10B981', bg: 'bg-[#10B981]/10' },
  'Passed': { color: '#10B981', bg: 'bg-[#10B981]/10' },
  'Blocked': { color: '#EF4444', bg: 'bg-[#EF4444]/10' },
};

const sevConfig = {
  CRITICAL: { color: '#EF4444', bg: 'bg-[#EF4444]/10', border: 'border-[#EF4444]/20' },
  HIGH: { color: '#F59E0B', bg: 'bg-[#F59E0B]/10', border: 'border-[#F59E0B]/20' },
};

export default function ChangeControl() {
  return (
    <div data-testid="change-control" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Changes This Week</div><div className="text-2xl font-bold text-[#F8FAFC]">5</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Pending Approval</div><div className="text-2xl font-bold text-[#F59E0B]">1</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Unauthorized</div><div className="text-2xl font-bold text-[#EF4444]">1</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Rules Active</div><div className="text-2xl font-bold text-[#10B981]">3</div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Change Log */}
        <div className="lg:col-span-2 glass-card overflow-hidden" data-testid="change-log">
          <div className="p-4 border-b border-[#1E2A45]">
            <h3 className="text-sm font-semibold text-[#F8FAFC]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Change Log</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#1E2A45]">
                {['ID', 'Time', 'Type', 'Target', 'Changed By', 'Status', 'Enforced'].map(h => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase py-2 px-3">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {changeLog.map(c => {
                  const ef = enforcedColors[c.enforced] || { color: '#64748B', bg: 'bg-[#64748B]/10' };
                  return (
                    <tr key={c.id} className={`border-b border-[#1E2A45]/50 ${c.enforced === 'Triggered Incident' ? 'bg-[#EF4444]/[0.03]' : ''}`}>
                      <td className="py-2 px-3 font-mono text-[#6366F1] text-xs">{c.id}</td>
                      <td className="py-2 px-3 text-[10px] font-mono text-[#475569] whitespace-nowrap">{c.time}</td>
                      <td className="py-2 px-3 text-xs text-[#F8FAFC]">{c.type}</td>
                      <td className="py-2 px-3 text-[10px] text-[#94A3B8] max-w-[160px] truncate">{c.target}</td>
                      <td className="py-2 px-3 text-[10px] font-mono text-[#64748B]">{c.changedBy}</td>
                      <td className="py-2 px-3"><span className={`text-[9px] px-2 py-0.5 rounded-full ${c.status === 'Unauthorized' ? 'bg-[#EF4444]/10 text-[#EF4444]' : c.status === 'Pending Approval' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>{c.status}</span></td>
                      <td className="py-2 px-3"><span className={`text-[9px] px-2 py-0.5 rounded-full ${ef.bg}`} style={{ color: ef.color }}>{c.enforced}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enforcement Rules */}
        <div className="glass-card p-5" data-testid="enforcement-rules">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={16} className="text-[#6366F1]" />
            <h3 className="text-sm font-semibold text-[#F8FAFC]">Enforcement Rules · Active</h3>
          </div>
          <div className="text-[9px] text-[#475569] mb-4">Supports: ISO 27001 A.12.5 · NIST 800-53 CM-3 · SOC 2 CC8.1</div>
          <div className="space-y-3">
            {rules.map((rule, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#6366F1]/10 text-[#6366F1]">Rule {i + 1}</span>
                  <span className="text-[9px] text-[#10B981]">✓ {rule.status}</span>
                </div>
                <div className="text-xs text-[#94A3B8] leading-relaxed mb-1.5">{rule.title}</div>
                <div className="text-[10px] text-[#64748B]">Currently: {rule.blocking}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Infrastructure Changes */}
      <div className="glass-card p-5" data-testid="infra-changes">
        <h3 className="text-sm font-semibold text-[#F8FAFC] mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>AI Infrastructure Changes</h3>
        <div className="text-xs text-[#64748B] mb-3">Evidence continuity checks for AI-related infrastructure modifications.</div>
        <div className="space-y-2">
          {infraChanges.map(ic => {
            const sev = sevConfig[ic.severity];
            return (
              <div key={ic.id} className="flex items-center justify-between p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <AlertTriangle size={14} style={{ color: sev.color }} className="flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-[#F8FAFC] truncate">{ic.desc}</div>
                    <div className="text-[10px] text-[#475569] font-mono">{ic.account} · {ic.time}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className={`text-[9px] font-mono px-2 py-0.5 rounded-full ${sev.bg} border ${sev.border}`} style={{ color: sev.color }}>{ic.severity}</span>
                  <button onClick={() => toast.info(`Reviewing: ${ic.desc}`)} className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">Review &rarr;</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
